import React, { useMemo } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import _ from 'underscore';

import { Constants, InsertionOrderUtils } from '@bingads-webui-clientcenter/entity-utils';
import { useDefaultStyles } from '@bingads-webui-react/with-default-styles';
import { getCivilDateFromDate } from '@bingads-webui-universal/temporal-utitlities';
import { formatCivilDate, formatCurrency, formatInteger } from '../../utils';
import { InsertionOrderTextKey, Severity } from '../../constants';
import { AmountNode, InsertionOrderItem, MessageItem } from './content-item-node';
import { getInsertionOrderCardStyles } from '../../styles/top-key-card';

const { BudgetEventType } = Constants;
const { isEndlessIOEndDate, isUnlimitedIOBudget } = InsertionOrderUtils;

export function InsertionOrderCard({
  i18n, currencyCode, rawData, abnormalStatus,
}) {
  const classes = useDefaultStyles(getInsertionOrderCardStyles);
  const { today, ExpireDate, RemainingBudget } = rawData;
  const expirationDate = useMemo(() => {
    if (!ExpireDate) {
      return null;
    }
    const date = getCivilDateFromDate(ExpireDate);
    return formatCivilDate({ i18n, civilDate: date, options: { date: 'medium' } });
  }, [ExpireDate, i18n]);

  const noActiveIO = useMemo(() => {
    const expiredMessage = _.find(abnormalStatus, item => item === BudgetEventType.InsertionOrderExpired) ? (
      <div className={classnames('no-active-io-message-item', classes.NoActiveMessageItem)}>
        <span className={classnames('no-active-io-message-item-prefix', classes.NoActiveMessageItemPrefix)}>
          {i18n.getString(_TL_('Your previous IO expires on {{expirationDate}}'), { expirationDate: '' })}
        </span>
        <MessageItem message={expirationDate} severity={Severity.Error} className={classes.FontSize14} />
      </div>
    ) : null;
    const remainingBudget = formatCurrency({
      i18n, value: 0, currencyCode, options: { compact: 'short' },
    });
    const exhaustedMessage = _.find(abnormalStatus, item => item === BudgetEventType.InsertionOrderBalanceDepleted) ? (
      <div className={classnames('no-active-io-message-item', classes.NoActiveMessageItem)}>
        <span className={classnames('no-active-io-message-item-prefix', classes.NoActiveMessageItemPrefix)}>
          {i18n.getString(_TL_('Your previous IO’s remaining budget is {{remainingBudget}}'), { remainingBudget: '' })}
        </span>
        <MessageItem message={remainingBudget} severity={Severity.Error} className={classes.FontSize14} />
      </div>
    ) : null;

    if (!expiredMessage && !exhaustedMessage && ExpireDate) {
      return null;
    }

    return (
      <div className={classnames('no-active-io', classes.NoActiveIO)}>
        <span className={classnames('no-active-io-name', classes.NoActiveName)}>{i18n.getString(_TL_('No active IO'))}</span>
        {expiredMessage}
        {exhaustedMessage}
      </div>
    );
  }, [ExpireDate, abnormalStatus, classes, currencyCode, expirationDate, i18n]);

  const remainingBudgetMessageConfig = useMemo(() => {
    if (!abnormalStatus) {
      return null;
    }
    for (let i = 0; i < abnormalStatus.length; i += 1) {
      const eventType = abnormalStatus[i];
      if (eventType === BudgetEventType.InsertionOrderExhaustingUrgent) {
        return {
          message: i18n.getString(_TL_('Exhaust in 7 days')),
          severity: Severity.Warning,
        };
      }
    }
    return null;
  }, [abnormalStatus, i18n]);

  const expireOnMessageConfig = useMemo(() => {
    if (!abnormalStatus || !ExpireDate) {
      return null;
    }
    for (let i = 0; i < abnormalStatus.length; i += 1) {
      const eventType = abnormalStatus[i];
      if (eventType === BudgetEventType.InsertionOrderExpiringUrgent) {
        const expireInDays = formatInteger({
          i18n,
          value: (ExpireDate - new Date(today.year, today.month - 1, today.day)) / (1000 * 3600 * 24),
          options: { withSign: 'auto' },
        });
        return {
          message: i18n.getString(_TL_('Expire in {{expireInDays}} days'), { expireInDays }),
          severity: Severity.Warning,
        };
      }
    }
    return null;
  }, [ExpireDate, abnormalStatus, i18n, today]);

  const noErrorNode = useMemo(() => {
    if (noActiveIO) {
      return null;
    }

    const remainingBudgetValue = isUnlimitedIOBudget(RemainingBudget)
      ? i18n.getString(InsertionOrderTextKey.Unlimited)
      : (<AmountNode
          i18n={i18n}
          currencyCode={currencyCode}
          amount={RemainingBudget}
      />);
    const expirationDateValue = isEndlessIOEndDate(ExpireDate)
      ? i18n.getString(InsertionOrderTextKey.NoEndDate)
      : expirationDate;
    return (
      <>
        <InsertionOrderItem
          i18n={i18n}
          itemKey={_TL_('Remaining budget')}
          itemValue={remainingBudgetValue}
          {...remainingBudgetMessageConfig}
        />
        <InsertionOrderItem
          i18n={i18n}
          itemKey={_TL_('Expire on')}
          itemValue={expirationDateValue}
          {...expireOnMessageConfig}
        />
      </>
    );
  }, [ExpireDate, RemainingBudget, currencyCode, expirationDate, expireOnMessageConfig, i18n, noActiveIO, remainingBudgetMessageConfig]);

  return (
    <>
      {noErrorNode}
      {noActiveIO}
    </>
  );
}

InsertionOrderCard.propTypes = {
  i18n: PropTypes.shape({
    getString: PropTypes.func.isRequired,
    formatCivilDate: PropTypes.func.isRequired,
  }).isRequired,
  currencyCode: PropTypes.string.isRequired,
  rawData: PropTypes.shape({
    today: PropTypes.shape({
      year: PropTypes.number.isRequired,
      month: PropTypes.number.isRequired,
      day: PropTypes.number.isRequired,
    }),
    RemainingBudget: PropTypes.number.isRequired,
    ExpireDate: PropTypes.shape({}),
  }).isRequired,
  abnormalStatus: PropTypes.arrayOf(PropTypes.number),
};

InsertionOrderCard.defaultProps = {
  abnormalStatus: [],
};
